import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(d: string | Date | null | undefined, fmt = 'dd/MM/yyyy HH:mm:ss'): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return fmt
    .replace('yyyy', date.getFullYear().toString())
    .replace('MM', pad(date.getMonth() + 1))
    .replace('dd', pad(date.getDate()))
    .replace('HH', pad(date.getHours()))
    .replace('mm', pad(date.getMinutes()))
    .replace('ss', pad(date.getSeconds()));
}

export function formatRelative(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  const diff = Date.now() - date.getTime();
  const abs = Math.abs(diff);
  const sec = Math.floor(abs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  const suf = diff > 0 ? 'atras' : 'em';
  if (sec < 60) return `${sec}s ${suf}`;
  if (min < 60) return `${min}min ${suf}`;
  if (hr < 24)  return `${hr}h ${suf}`;
  return `${day}d ${suf}`;
}

export function truncate(s: string | null | undefined, len: number): string {
  if (!s) return '';
  return s.length > len ? s.slice(0, len - 3) + '…' : s;
}

// Extrai um id estável a partir de payloads do backend Mongo (BsonDocument expandido).
// Aceita: string hex direto, ObjectId-as-string, ou objeto {timestamp, machine, pid, increment}.
// Usado para chaves de lista (key={...}) e para construir URLs.
export function bsonId(value: unknown, fallback: string | number = ''): string {
  if (value == null) return String(fallback);
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const o = value as { $oid?: string; toString?: () => string; timestamp?: number; machine?: number; pid?: number; increment?: number };
    if (typeof o.$oid === 'string') return o.$oid;
    if (typeof o.toString === 'function') {
      const s = o.toString();
      if (s && s !== '[object Object]') return s;
    }
    // BsonDocument.ToDictionary() expande ObjectId em campos numericos: serializa concatenando
    if (typeof o.timestamp === 'number') {
      const t = (o.timestamp >>> 0).toString(16).padStart(8, '0');
      const m = ((o.machine ?? 0) >>> 0).toString(16).padStart(6, '0').slice(0, 6);
      const p = ((o.pid ?? 0) >>> 0).toString(16).padStart(4, '0').slice(0, 4);
      const i = ((o.increment ?? 0) >>> 0).toString(16).padStart(6, '0').slice(0, 6);
      return (t + m + p + i).slice(0, 24);
    }
  }
  return String(fallback);
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function copyToClipboard(text: string) {
  return navigator.clipboard.writeText(text);
}
